import { useAppDispatch } from 'legacy/lut/hooks/useAppDispatch'
import { useEffect, useMemo, useState } from 'react'
import { useCallback } from 'react'

import { PartnerFormMenuItems, PartnerStatus } from 'clickNgo/entities/partner/configs'
import { shouldCopyAddress } from 'clickNgo/entities/partner/libs/helpers'
import { useCheckReadOnlyAccess } from 'clickNgo/hooks/access'
import { initFormData } from 'cng-common/features/form'

import { partnerEditorDefaultSetting, PartnerEditorFormKeys } from '../configs'
import { IPartnerEditorData } from '../models'

interface IUseInitPartnerEditorSettingParams extends IPartnerEditorData {
  defaultMenuItem?: PartnerFormMenuItems
  isReadOnly?: boolean
}

export const useInitPartnerEditorSetting = ({
  partner,
  partnerFlags,
  partnerAttachments,
  isReadOnly,
  defaultMenuItem = PartnerFormMenuItems.Basic,
}: IUseInitPartnerEditorSettingParams) => {
  const [isInitialized, setIsInitialized] = useState(false)
  const dispatch = useAppDispatch()

  const doCopyBasicAddress = useMemo(() => shouldCopyAddress(partner), [partner])

  const initialData = useMemo(
    () => ({
      ...partnerEditorDefaultSetting,
      actualData: { partner, partnerFlags, partnerAttachments },
      currentMenuItem: defaultMenuItem,
      doCopyBasicAddress,
    }),
    [defaultMenuItem, doCopyBasicAddress, partner, partnerAttachments, partnerFlags]
  )

  const isReadOnlyRoleAccess = useCheckReadOnlyAccess()

  const resetPartnerEditorSetting = useCallback(() => {
    dispatch(initFormData({ formKey: PartnerEditorFormKeys.Setting, formData: null }))
    setIsInitialized(false)
  }, [dispatch])

  useEffect(() => {
    if (!isInitialized) {
      const isRequiredDataExist = initialData.actualData.partner

      const isInactive = initialData.actualData.partner.status === PartnerStatus.Inactive

      if (isRequiredDataExist) {
        dispatch(
          initFormData({
            formKey: PartnerEditorFormKeys.Setting,
            formData: {
              ...initialData,
              isInactive,
              isReadOnly: isReadOnlyRoleAccess || isInactive || isReadOnly,
            },
          })
        )
        setIsInitialized(true)
      } else {
        setIsInitialized(false)
      }
    }
  }, [dispatch, initialData, isInitialized, isReadOnly, isReadOnlyRoleAccess])

  return { isInitialized, resetPartnerEditorSetting }
}
